import { Clear } from '@mui/icons-material';
import { Box, type BoxProps, IconButton, Stack, Typography } from '@mui/material';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { type TreeNode, useEditorActiveNode, useEditorStore, useNodeState } from '../Editor';

type RecursiveProps = {
  wrapperProps?: Omit<BoxProps, 'children'>;
};

type Props = {
  children?: React.ReactNode;
} & RecursiveProps;

const ConfigElement = ({ node }: { node: TreeNode | null | undefined; state: TreeNode['state'] }) => {
  const { activeNodeRoot, activeNode, setActiveNode } = useEditorActiveNode();

  const whatActiveAmI = useMemo(() => {
    if (!node) {
      return null;
    }

    if (activeNodeRoot?.id === node?.id) {
      return 'root';
    }

    if (activeNode?.id !== node?.id && activeNode?.nodePath?.includes(node?.nodePath ?? '')) {
      return 'child';
    }

    if (activeNode?.id === node?.id) {
      return 'active';
    }

    return null;
  }, [activeNodeRoot, activeNode, node]);

  if (!node) {
    return null;
  }

  const onRemoveHandler = useCallback(() => {
    if (node?.folder?.onRemove) {
      node.folder.onRemove();
      return;
    }
  }, [node?.folder?.onRemove]);

  return (
    <Box
      className={`config-element level-${node?.nodePath?.split(',')?.length} ${whatActiveAmI ? 'active' : ''}`}
      style={{ marginLeft: `${8 * (node?.nodePath?.split(',')?.length ?? 0)}px` }}
      sx={{
        ...(!!whatActiveAmI && {
          backgroundColor: 'var(--backend-s-col)',
        }),
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: !whatActiveAmI ? '#f0f0f0' : 'var(--backend-s-col)',
        },
      }}
      onClick={() => {
        if (activeNode?.id !== node.id) {
          setActiveNode(node);
        }
      }}
    >
      <Stack direction="row" spacing={1} justifyContent="space-between" width="100%">
        <Stack direction="row" spacing={0.5} alignItems="center">
          {node?.folder?.icon && <IconButton size="small">{node?.folder?.icon}</IconButton>}
          <Typography variant="body1">{node?.folder?.title ?? node.id}</Typography>
        </Stack>
        <div>
          {(!!node?.folder?.removable || typeof node?.folder?.onRemove === 'function') && (
            <IconButton size="small" onClick={onRemoveHandler} className="remove-node">
              <Clear />
            </IconButton>
          )}
        </div>
      </Stack>
    </Box>
  );
};

type EditorConfigContextType = {
  node: TreeNode | null;
};

const EditorConfigContext = createContext<EditorConfigContextType | null>(null);

export const EditorTreeUI = (props: Props) => {
  const nodes = useEditorStore((store) => store.nodes);

  const rootNodes = useMemo(() => {
    const elements: TreeNode[] = [];

    if (nodes.size > 0) {
      for (const node of nodes.values()) {
        if (!node.parentId) {
          elements.push(node);
        }
      }
    }

    return elements;
  }, [nodes]);

  const boxProps: BoxProps = useMemo(() => {
    const defaultProps = {
      sx: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      },
    };

    return {
      ...defaultProps,
      ...props?.wrapperProps,
    };
  }, [props?.wrapperProps]);

  return (
    <Box {...boxProps}>
      {rootNodes?.map((node, idx) => (
        <EditorConfigContext.Provider
          key={`${node.id}_${idx}`}
          value={{
            node,
          }}
        >
          <NodeRootAndChildren />
        </EditorConfigContext.Provider>
      ))}
    </Box>
  );
};

const NodeRootAndChildren = (props: RecursiveProps) => {
  const ctx = useContext(EditorConfigContext);
  const state = useNodeState(ctx?.node?.id);

  const boxProps: BoxProps = useMemo(() => {
    const defaultProps = {
      sx: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      },
    };

    return {
      ...defaultProps,
      ...props?.wrapperProps,
    };
  }, [props?.wrapperProps]);

  const childrenElements = useMemo(() => {
    if (!ctx?.node) {
      return [];
    }

    const elements: TreeNode[] = [];

    if (ctx?.node.childrenNodes.size > 0) {
      for (const child of ctx.node.childrenNodes.values()) {
        elements.push(child);
      }
    }

    return elements;
  }, [ctx?.node]);

  if (!ctx?.node) {
    return null;
  }

  return (
    <>
      <ConfigElement node={ctx?.node} state={state} />
      <Box {...boxProps}>
        {childrenElements?.map((child, id) => (
          <EditorConfigContext.Provider
            key={`${child.id}_${id}`}
            value={{
              node: child,
            }}
          >
            <NodeRootAndChildren />
          </EditorConfigContext.Provider>
        ))}
      </Box>
    </>
  );
};
